import { useMemo, useState } from "react";
import { useTheme } from "next-themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { adminFetch, adminFetchLong, useAdminSession } from "@/hooks/use-admin-session";
import { Bell, LayoutGrid, List, Play, Trash2, Plus, X, Pencil, Search } from "lucide-react";

type FrekansObj = { base: number; beat?: number };

type FrekansSet = {
  id: string;
  baslik: string;
  aciklama?: string;
  frekanslar: FrekansObj[];
  sure_saniye: number;
  ses_url?: string;
  gorsel_url?: string;
  tags: string[];
  xp?: number;
  program_gun?: number;
  is_premium?: boolean;
  price?: number;
  store_product_id?: string | null;
  nature_sound_url?: string | null;
  nature_sound_label?: string | null;
  kategori_id?: string;
  created_at: string;
};

type FrekansRow = { base: string; beat: string; binaural: boolean };

const NATURE_SOUNDS = [
  { label: "Yok", file: null },
  { label: "Cıcır Böceği", file: "circir-bocegi.mp3" },
  { label: "Dalga", file: "dalga.mp3" },
  { label: "Gök Gürültülü Yağmur", file: "gokgurultulu-yagmur.mp3" },
  { label: "Kuş", file: "kus.mp3" },
  { label: "Orman", file: "orman.mp3" },
  { label: "Rüzgar", file: "ruzgar-doga.mp3" },
  { label: "Yağmur", file: "yagmur.mp3" },
  { label: "Yağmur 2", file: "yagmur2.mp3" },
  { label: "Yağmur 3", file: "yagmur3.mp3" },
] as const;

const NATURE_SOUND_BASE = "https://altlhxbmfnhvzsrsdpsw.supabase.co/storage/v1/object/public/audio/doga-sesleri/";

function natureFileToUrl(file: string | null): string | null {
  return file ? `${NATURE_SOUND_BASE}${file}` : null;
}

function natureUrlToFile(url: string | null | undefined): string | null {
  if (!url) return null;
  const file = url.split("/").pop() ?? null;
  return NATURE_SOUNDS.some((s) => s.file === file) ? file : null;
}

async function fetchFrekanslar(): Promise<FrekansSet[]> {
  const res = await adminFetch("/api/admin/frekans-setleri");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Frekans setleri alınamadı.");
  return Array.isArray(json?.data) ? json.data : [];
}

async function createFrekans(payload: {
  baslik: string;
  aciklama: string;
  frekanslar: FrekansObj[];
  sure_saniye: number;
  gorsel_url?: string;
  tags: string[];
  xp: number;
  program_gun?: number;
  is_premium?: boolean;
  price?: number;
  store_product_id?: string | null;
  nature_sound_url?: string | null;
  nature_sound_label?: string | null;
  kategori_id?: string | null;
  title_en?: string | null;
  title_es?: string | null;
  description_en?: string | null;
  description_es?: string | null;
}): Promise<void> {
  const res = await adminFetchLong("/api/admin/frekans-setleri", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Frekans seti oluşturulamadı.");
}

async function uploadImage(file: File): Promise<string> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("bucket", "numeroloji-media");
  formData.append("folder", "frekans-setleri");
  const res = await adminFetch("/api/admin/upload", { method: "POST", body: formData });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Görsel yüklenemedi.");
  return json.url;
}

async function uploadSound(file: File): Promise<string> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("bucket", "audio");
  formData.append("folder", "doga-sesleri");
  const res = await adminFetch("/api/admin/upload", { method: "POST", body: formData });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Ses yüklenemedi.");
  return json.url;
}

async function deleteFrekans(id: string): Promise<void> {
  const res = await adminFetch(`/api/admin/frekans-setleri/${encodeURIComponent(id)}`, { method: "DELETE" });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) throw new Error(json?.error ?? "Frekans seti silinemedi.");
}

const emptyForm = { baslik: "", aciklama: "", sure_saniye: "30", gorsel_url: "", tagInput: "", xp: "0", program_gun: "", is_premium: false, price: "0", store_product_id: "" as string | null, nature_sound_file: "", nature_sound_url: "", nature_sound_label: "", kategori_id: "", title_en: "", title_es: "", description_en: "", description_es: "" };
type FormState = typeof emptyForm & { tags: string[] };

// Google Play / RevenueCat'te tanımlı 3 sabit tek seferlik içerik fiyat kademesi.
const PRICE_TIERS = [
  { price: 999, storeProductId: 'icerik_999', label: '₺999,00 (icerik_999)' },
  { price: 1299, storeProductId: 'icerik_1299', label: '₺1.299,00 (icerik_1299)' },
  { price: 1499, storeProductId: 'icerik_1499', label: '₺1.499,00 (icerik_1499)' },
] as const;
const emptyFormFull = (): FormState => ({ ...emptyForm, tags: [] });
const emptyRow = (): FrekansRow => ({ base: "", beat: "", binaural: false });

export default function AdminNumerolojiFrekanslar() {
  const queryClient = useQueryClient();
  const { theme, setTheme, systemTheme } = useTheme();
  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(emptyFormFull());
  const [formI18nLang, setFormI18nLang] = useState<'en' | 'es' | null>(null);
  const [editI18nLang, setEditI18nLang] = useState<'en' | 'es' | null>(null);
  const [editingItem, setEditingItem] = useState<FrekansSet | null>(null);
  const [frekansListesi, setFrekansListesi] = useState<FrekansRow[]>([emptyRow()]);
  const [playingUrl, setPlayingUrl] = useState<string | null>(null);
  const [audio] = useState(() => (typeof Audio !== "undefined" ? new Audio() : null));
  const [formImgLoading, setFormImgLoading] = useState(false);
  const [editImgLoading, setEditImgLoading] = useState(false);
  const [formSoundLoading, setFormSoundLoading] = useState(false);
  const [editSoundLoading, setEditSoundLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<"newest" | "title-asc" | "duration-asc" | "duration-desc">("newest");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  async function handleFormImagePick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFormImgLoading(true);
    try {
      const url = await uploadImage(file);
      setForm((f) => ({ ...f, gorsel_url: url }));
    } catch (err: any) {
      alert(err.message ?? "Görsel yüklenemedi.");
    } finally {
      setFormImgLoading(false);
      e.target.value = "";
    }
  }

  async function handleEditImagePick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setEditImgLoading(true);
    try {
      const url = await uploadImage(file);
      setEditingItem((p) => p && { ...p, gorsel_url: url });
    } catch (err: any) {
      alert(err.message ?? "Görsel yüklenemedi.");
    } finally {
      setEditImgLoading(false);
      e.target.value = "";
    }
  }

  async function handleFormSoundPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFormSoundLoading(true);
    try {
      const url = await uploadSound(file);
      setForm((f) => ({ ...f, nature_sound_url: url, nature_sound_label: file.name, nature_sound_file: "__custom" }));
    } catch (err: any) {
      alert(err.message ?? "Ses yüklenemedi.");
    } finally {
      setFormSoundLoading(false);
      e.target.value = "";
    }
  }

  async function handleEditSoundPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setEditSoundLoading(true);
    try {
      const url = await uploadSound(file);
      setEditingItem((p) => p && { ...p, nature_sound_url: url, nature_sound_label: file.name });
    } catch (err: any) {
      alert(err.message ?? "Ses yüklenemedi.");
    } finally {
      setEditSoundLoading(false);
      e.target.value = "";
    }
  }

  const listQuery = useQuery({
    queryKey: ["admin-frekans-setleri"],
    queryFn: fetchFrekanslar,
    enabled: authenticated,
    staleTime: 0,
  });

  const { data: frekansKatData } = useQuery({
    queryKey: ['kategoriler', 'frekans'],
    queryFn: () => adminFetch('/api/admin/kategoriler?tur=frekans').then(r => r.json()),
    enabled: authenticated,
  });
  const frekansKategoriler: any[] = frekansKatData?.items ?? [];

  const createMutation = useMutation({
    mutationFn: createFrekans,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-frekans-setleri"] });
      setShowForm(false);
      setForm(emptyFormFull());
      setFrekansListesi([emptyRow()]);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: { id: string; baslik: string; aciklama: string; sure_saniye: number; gorsel_url?: string; tags: string[]; xp: number; program_gun?: number; is_premium?: boolean; price?: number; store_product_id?: string | null; nature_sound_url?: string | null; nature_sound_label?: string | null; kategori_id?: string | null; title_en?: string | null; title_es?: string | null; description_en?: string | null; description_es?: string | null }) => {
      const res = await adminFetch(`/api/admin/frekans-setleri/${payload.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = (await res.json().catch(() => null)) as any;
      if (!res.ok) throw new Error(json?.error ?? "Güncellenemedi");
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-frekans-setleri"] });
      setEditingItem(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteFrekans,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-frekans-setleri"] });
    },
  });

  function handlePlay(url: string) {
    if (!audio) return;
    if (playingUrl === url) {
      audio.pause();
      audio.currentTime = 0;
      setPlayingUrl(null);
    } else {
      audio.src = url;
      audio.play();
      setPlayingUrl(url);
      audio.onended = () => setPlayingUrl(null);
    }
  }

  function updateRow(idx: number, patch: Partial<FrekansRow>) {
    setFrekansListesi((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  function removeRow(idx: number) {
    setFrekansListesi((prev) => prev.filter((_, i) => i !== idx));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (createMutation.isPending) return;
    const frekanslar: FrekansObj[] = frekansListesi
      .map((r) => {
        const base = parseFloat(r.base);
        if (isNaN(base) || base <= 0) return null;
        if (r.binaural) {
          const beat = parseFloat(r.beat);
          return isNaN(beat) || beat <= 0 ? { base } : { base, beat };
        }
        return { base };
      })
      .filter((f) => f !== null) as FrekansObj[];

    if (!form.baslik.trim() || frekanslar.length === 0) return;
    createMutation.mutate({
      baslik: form.baslik.trim(),
      aciklama: form.aciklama.trim(),
      frekanslar,
      sure_saniye: parseFloat(form.sure_saniye) || 30,
      ...(form.gorsel_url.trim() ? { gorsel_url: form.gorsel_url.trim() } : {}),
      tags: form.tags,
      xp: parseInt(form.xp) || 0,
      ...(form.program_gun.trim() ? { program_gun: parseInt(form.program_gun) } : {}),
      is_premium: form.is_premium,
      price: parseInt(form.price) || 0,
      store_product_id: form.store_product_id || null,
      nature_sound_url: form.nature_sound_file === "__custom" ? (form.nature_sound_url || null) : natureFileToUrl(form.nature_sound_file || null),
      nature_sound_label: form.nature_sound_file === "__custom" ? (form.nature_sound_label || null) : (form.nature_sound_file ? (NATURE_SOUNDS.find((s) => s.file === form.nature_sound_file)?.label ?? null) : null),
      kategori_id: form.kategori_id || null,
      title_en: form.title_en.trim() || null,
      title_es: form.title_es.trim() || null,
      description_en: form.description_en.trim() || null,
      description_es: form.description_es.trim() || null,
    });
  }

  const items = listQuery.data ?? [];
  const resolvedTheme = theme === "system" ? systemTheme : theme;
  const isDark = resolvedTheme === "dark";

  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = q
      ? items.filter((f) => {
          const hay = `${f.baslik} ${f.aciklama ?? ""} ${(f.tags ?? []).join(" ")}`.toLowerCase();
          return hay.includes(q);
        })
      : items;
    const sorted = [...rows];
    if (sort === "title-asc") {
      sorted.sort((a, b) => String(a.baslik || "").localeCompare(String(b.baslik || ""), "tr"));
    } else if (sort === "duration-asc") {
      sorted.sort((a, b) => Number(a.sure_saniye || 0) - Number(b.sure_saniye || 0));
    } else if (sort === "duration-desc") {
      sorted.sort((a, b) => Number(b.sure_saniye || 0) - Number(a.sure_saniye || 0));
    }
    // "newest" = server order (created_at DESC)
    return sorted;
  }, [items, search, sort]);

  if (sessionQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Yükleniyor...</div>;
  }

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }


  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />

      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        <header className="h-16 border-b border-slate-200/70 bg-white/70 px-4 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/40 sm:px-6">
          <div className="flex h-full items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <NumerolojiAdminMobileSidebar
                mode="links"
                isDark={isDark}
                onToggleTheme={() => setTheme(isDark ? "light" : "dark")}
              />
              <div className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-lg">
                Frekans Yönetimi
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300"
                aria-label="Bildirimler"
              >
                <Bell className="h-4 w-4" />
              </button>
              <Button
                variant="outline"
                className="rounded-full"
                size="sm"
                onClick={() => logoutMutation.mutate()}
                disabled={logoutMutation.isPending}
              >
                Çıkış
              </Button>
            </div>
          </div>
        </header>

        <div className="min-w-0 flex-1 p-4 sm:p-6">
          {listQuery.isLoading ? (
            <div className="text-sm text-muted-foreground">Yükleniyor...</div>
          ) : listQuery.isError ? (
            <div className="text-sm text-destructive">{(listQuery.error as Error).message}</div>
          ) : (
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">Frekans Setleri</div>
                  <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Sinüs dalgası tabanlı frekans setleri oluşturun ve WAV olarak yayınlayın.
                  </div>
                </div>
                <Button size="sm" className="rounded-full" onClick={() => setShowForm(true)}>
                  <Plus className="mr-1 h-4 w-4" />
                  Yeni Frekans Seti
                </Button>
              </div>

              {/* Yeni ekle formu */}
              {showForm && (
                <form
                  onSubmit={handleSubmit}
                  className="mt-6 rounded-2xl border border-blue-200 bg-blue-50/50 p-5 dark:border-blue-800/40 dark:bg-blue-950/20"
                >
                  <div className="mb-4 flex items-center justify-between">
                    <div className="font-semibold text-slate-900 dark:text-white">Yeni Frekans Seti</div>
                    <button type="button" onClick={() => { setShowForm(false); setForm(emptyFormFull()); setFrekansListesi([emptyRow()]); }}>
                      <X className="h-4 w-4 text-slate-500" />
                    </button>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Başlık *</label>
                      <input
                        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                        value={form.baslik}
                        onChange={(e) => setForm((f) => ({ ...f, baslik: e.target.value }))}
                        placeholder="örn: Theta Dalga Seti"
                        required
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Açıklama</label>
                      <textarea
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                        rows={2}
                        value={form.aciklama}
                        onChange={(e) => setForm((f) => ({ ...f, aciklama: e.target.value }))}
                        placeholder="Bu frekans setinin etkisi..."
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <div className="mb-2 flex items-center gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Çeviriler</span>
                        <div className="flex gap-1">
                          {(["en", "es"] as const).map((lang) => (
                            <button
                              key={lang}
                              type="button"
                              onClick={() => setFormI18nLang((p) => p === lang ? null : lang)}
                              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${formI18nLang === lang ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400"}`}
                            >
                              {lang === "en" ? "🇬🇧 EN" : "🇪🇸 ES"}
                            </button>
                          ))}
                        </div>
                      </div>
                      {formI18nLang && (
                        <div className="grid gap-3">
                          <div>
                            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Başlık ({formI18nLang.toUpperCase()})</label>
                            <input
                              className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                              value={formI18nLang === "en" ? form.title_en : form.title_es}
                              onChange={(e) => setForm((f) => ({ ...f, [`title_${formI18nLang}`]: e.target.value }))}
                              placeholder={formI18nLang === "en" ? "English title..." : "Título en español..."}
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Açıklama ({formI18nLang.toUpperCase()})</label>
                            <textarea
                              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                              rows={2}
                              value={formI18nLang === "en" ? form.description_en : form.description_es}
                              onChange={(e) => setForm((f) => ({ ...f, [`description_${formI18nLang}`]: e.target.value }))}
                              placeholder={formI18nLang === "en" ? "English description..." : "Descripción en español..."}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="sm:col-span-2">
                      <div className="mb-2 flex items-center justify-between">
                        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Frekanslar *</label>
                        <button
                          type="button"
                          onClick={() => setFrekansListesi((prev) => [...prev, emptyRow()])}
                          className="flex items-center gap-1 rounded-lg bg-blue-600/10 px-2 py-1 text-xs font-semibold text-blue-600 hover:bg-blue-600/20 dark:text-blue-300"
                        >
                          <Plus className="h-3 w-3" />
                          Frekans Ekle
                        </button>
                      </div>

                      <div className="flex flex-col gap-2">
                        {frekansListesi.map((row, idx) => (
                          <div key={idx} className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900">
                            <div className="flex flex-1 items-center gap-2 min-w-0">
                              <input
                                type="number"
                                min="0.01"
                                step="any"
                                className="h-9 w-28 rounded-lg border border-slate-200 bg-slate-50 px-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                                placeholder="Base Hz"
                                value={row.base}
                                onChange={(e) => updateRow(idx, { base: e.target.value })}
                                required
                              />
                              <label className="flex items-center gap-1.5 cursor-pointer select-none text-xs text-slate-600 dark:text-slate-300">
                                <input
                                  type="checkbox"
                                  className="h-3.5 w-3.5 rounded accent-blue-600"
                                  checked={row.binaural}
                                  onChange={(e) => updateRow(idx, { binaural: e.target.checked })}
                                />
                                Binaural
                              </label>
                              {row.binaural && (
                                <input
                                  type="number"
                                  min="0.01"
                                  step="any"
                                  className="h-9 w-24 rounded-lg border border-slate-200 bg-slate-50 px-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                                  placeholder="Beat Hz"
                                  value={row.beat}
                                  onChange={(e) => updateRow(idx, { beat: e.target.value })}
                                />
                              )}
                            </div>
                            {frekansListesi.length > 1 && (
                              <button type="button" onClick={() => removeRow(idx)} className="rounded-lg p-1 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30">
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="mt-1 text-[11px] text-slate-400">Her frekans sırayla çalınır. Binaural: sol kanal = Base, sağ kanal = Base + Beat.</div>
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Her frekans süresi (saniye) *
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="3600"
                        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                        value={form.sure_saniye}
                        onChange={(e) => setForm((f) => ({ ...f, sure_saniye: e.target.value }))}
                        required
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                        XP Değeri
                      </label>
                      <input
                        type="number"
                        min="0"
                        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                        value={form.xp}
                        onChange={(e) => setForm((f) => ({ ...f, xp: e.target.value }))}
                        placeholder="0"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Ürün Kademesi
                      </label>
                      <select
                        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                        value={form.store_product_id ?? (parseInt(form.price) ? "custom" : "free")}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === "free") {
                            setForm((f) => ({ ...f, price: "0", store_product_id: null }));
                          } else if (v === "custom") {
                            setForm((f) => ({ ...f, store_product_id: null }));
                          } else {
                            const tier = PRICE_TIERS.find((t) => t.storeProductId === v);
                            if (tier) setForm((f) => ({ ...f, price: String(tier.price), store_product_id: tier.storeProductId }));
                          }
                        }}
                      >
                        <option value="free">Ücretsiz</option>
                        {PRICE_TIERS.map((t) => (
                          <option key={t.storeProductId} value={t.storeProductId}>{t.label}</option>
                        ))}
                        <option value="custom">Serbest / Elle gir</option>
                      </select>
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Fiyat (₺) — 0 ise ücretsiz
                      </label>
                      <input
                        type="number"
                        min="0"
                        disabled={Boolean(form.store_product_id)}
                        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/30 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                        value={form.price}
                        onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                        placeholder="0"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Program Günü (opsiyonel)
                      </label>
                      <input
                        type="number"
                        min="0"
                        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                        value={form.program_gun}
                        onChange={(e) => setForm((f) => ({ ...f, program_gun: e.target.value }))}
                        placeholder="örn: 7"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Görsel (opsiyonel)
                      </label>
                      <div className="flex items-center gap-3">
                        {form.gorsel_url && (
                          <img src={form.gorsel_url} alt="" className="h-14 w-14 flex-shrink-0 rounded-xl object-cover" />
                        )}
                        <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
                          {formImgLoading ? "Yükleniyor…" : form.gorsel_url ? "Değiştir" : "Görsel Seç"}
                          <input type="file" accept="image/*" className="hidden" onChange={handleFormImagePick} disabled={formImgLoading} />
                        </label>
                        {form.gorsel_url && (
                          <button type="button" className="text-rose-400 hover:text-rose-600" onClick={() => setForm((f) => ({ ...f, gorsel_url: "" }))}>
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Etiketler</label>
                      <div className="flex flex-wrap gap-1.5 rounded-xl border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900">
                        {form.tags.map((tag, i) => (
                          <span key={i} className="flex items-center gap-1 rounded-full bg-blue-600/10 px-2 py-0.5 text-xs font-semibold text-blue-600 dark:text-blue-300">
                            {tag}
                            <button type="button" onClick={() => setForm((f) => ({ ...f, tags: f.tags.filter((_, ti) => ti !== i) }))} className="leading-none opacity-70 hover:opacity-100">×</button>
                          </span>
                        ))}
                        <input
                          className="min-w-[120px] flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400 dark:text-white"
                          placeholder="Etiket yaz, Enter'a bas..."
                          value={form.tagInput}
                          onChange={(e) => setForm((f) => ({ ...f, tagInput: e.target.value }))}
                          onKeyDown={(e) => {
                            if ((e.key === "Enter" || e.key === ",") && form.tagInput.trim()) {
                              e.preventDefault();
                              const val = form.tagInput.trim().replace(/,$/, "");
                              if (val && !form.tags.includes(val)) setForm((f) => ({ ...f, tags: [...f.tags, val], tagInput: "" }));
                              else setForm((f) => ({ ...f, tagInput: "" }));
                            }
                          }}
                        />
                      </div>
                      <div className="mt-1 text-[11px] text-slate-400">Enter veya virgül ile ekle.</div>
                    </div>

                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Kategori</label>
                      <select
                        key={frekansKategoriler.length}
                        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                        value={form.kategori_id}
                        onChange={(e) => setForm((f) => ({ ...f, kategori_id: e.target.value }))}
                      >
                        <option value="">— Kategori seç —</option>
                        {frekansKategoriler.map((k: any) => (
                          <option key={k.id} value={k.id}>{k.isim}</option>
                        ))}
                      </select>
                    </div>

                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Doğa Sesi</label>
                      <div className="flex items-center gap-2">
                        <select
                          className="h-10 flex-1 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                          value={form.nature_sound_file}
                          onChange={(e) => setForm((f) => ({ ...f, nature_sound_file: e.target.value, nature_sound_url: "", nature_sound_label: "" }))}
                        >
                          {NATURE_SOUNDS.map((s) => (
                            <option key={s.file ?? "__none"} value={s.file ?? ""}>{s.label}</option>
                          ))}
                          {form.nature_sound_file === "__custom" && (
                            <option value="__custom">Özel ses ({form.nature_sound_label})</option>
                          )}
                        </select>
                        <label className="flex cursor-pointer items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 whitespace-nowrap">
                          {formSoundLoading ? "Yükleniyor…" : "Ses Yükle"}
                          <input type="file" accept="audio/*" className="hidden" onChange={handleFormSoundPick} disabled={formSoundLoading} />
                        </label>
                      </div>
                      {(form.nature_sound_file && form.nature_sound_file !== "__custom") && (
                        <div className="mt-1 text-[11px] text-slate-400 break-all">{natureFileToUrl(form.nature_sound_file)}</div>
                      )}
                      {form.nature_sound_file === "__custom" && form.nature_sound_url && (
                        <div className="mt-1 text-[11px] text-slate-400 break-all">{form.nature_sound_url}</div>
                      )}
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">Premium</div>
                        <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Sadece premium üyeler görebilir.</div>
                      </div>
                      <label className="relative inline-flex cursor-pointer items-center">
                        <input
                          type="checkbox"
                          className="peer sr-only"
                          checked={form.is_premium}
                          onChange={(e) => setForm((f) => ({ ...f, is_premium: e.target.checked }))}
                        />
                        <div className="h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-amber-500 peer-checked:after:translate-x-full dark:bg-slate-800" />
                      </label>
                    </div>
                  </div>

                  {createMutation.isError && (
                    <div className="mt-3 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                      {(createMutation.error as Error).message}
                    </div>
                  )}

                  <div className="mt-4 flex gap-2">
                    <Button type="submit" size="sm" className="rounded-full" disabled={createMutation.isPending}>
                      {createMutation.isPending ? "WAV Üretiliyor…" : "Oluştur & WAV Üret"}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                      onClick={() => { setShowForm(false); setForm(emptyFormFull()); }}
                    >
                      İptal
                    </Button>
                  </div>
                </form>
              )}

              <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    className="h-11 w-full rounded-2xl bg-slate-100 pl-10 pr-4 text-sm text-slate-900 outline-none ring-blue-600/20 focus:ring-4 dark:bg-slate-800 dark:text-slate-50"
                    placeholder="Başlık, açıklama, etiket..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-2">
                    <div className="text-xs font-medium text-slate-500 dark:text-slate-400">Sırala:</div>
                    <select
                      className="h-10 rounded-full border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 outline-none dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-200"
                      value={sort}
                      onChange={(e) => setSort(e.target.value as any)}
                    >
                      <option value="newest">En Yeni</option>
                      <option value="title-asc">Başlık (A-Z)</option>
                      <option value="duration-asc">Süre (Artan)</option>
                      <option value="duration-desc">Süre (Azalan)</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-1 rounded-2xl border border-slate-200 bg-white p-1 dark:border-slate-800 dark:bg-slate-950/40">
                    <button
                      type="button"
                      className={cn("grid h-8 w-8 place-items-center rounded-xl transition-colors", viewMode === "grid" ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900" : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100")}
                      onClick={() => setViewMode("grid")}
                      title="Grid görünümü"
                    >
                      <LayoutGrid className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      className={cn("grid h-8 w-8 place-items-center rounded-xl transition-colors", viewMode === "list" ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900" : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100")}
                      onClick={() => setViewMode("list")}
                      title="Liste görünümü"
                    >
                      <List className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              {deleteMutation.isError && (
                <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                  {(deleteMutation.error as Error).message}
                </div>
              )}

              {/* Düzenleme Modalı */}
              {editingItem && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      updateMutation.mutate({
                        id: editingItem.id,
                        baslik: editingItem.baslik,
                        aciklama: editingItem.aciklama ?? "",
                        sure_saniye: editingItem.sure_saniye,
                        ...(editingItem.gorsel_url ? { gorsel_url: editingItem.gorsel_url } : {}),
                        tags: editingItem.tags ?? [],
                        xp: editingItem.xp ?? 0,
                        ...(editingItem.program_gun != null ? { program_gun: editingItem.program_gun } : {}),
                        is_premium: editingItem.is_premium ?? false,
                        price: editingItem.price ?? 0,
                        store_product_id: (editingItem as any).store_product_id || null,
                        nature_sound_url: editingItem.nature_sound_url ?? null,
                        nature_sound_label: editingItem.nature_sound_label ?? null,
                        kategori_id: editingItem.kategori_id || null,
                        title_en: (editingItem as any).title_en || null,
                        title_es: (editingItem as any).title_es || null,
                        description_en: (editingItem as any).description_en || null,
                        description_es: (editingItem as any).description_es || null,
                      });
                    }}
                    className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900"
                  >
                    <div className="flex flex-shrink-0 items-center justify-between rounded-t-2xl border-b border-slate-200 bg-white p-6 pb-4 dark:border-slate-800 dark:bg-slate-900">
                      <div className="font-semibold text-slate-900 dark:text-white">Frekans Setini Düzenle</div>
                      <button type="button" onClick={() => setEditingItem(null)}>
                        <X className="h-4 w-4 text-slate-500" />
                      </button>
                    </div>

                    <div className="overflow-y-auto p-6">
                    <div className="grid gap-4">
                      <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Başlık *</label>
                        <input
                          className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                          value={editingItem.baslik}
                          onChange={(e) => setEditingItem((p) => p && { ...p, baslik: e.target.value })}
                          required
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Açıklama</label>
                        <textarea
                          className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                          rows={2}
                          value={editingItem.aciklama ?? ""}
                          onChange={(e) => setEditingItem((p) => p && { ...p, aciklama: e.target.value })}
                        />
                      </div>

                      <div>
                        <div className="mb-2 flex items-center gap-2">
                          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Çeviriler</span>
                          <div className="flex gap-1">
                            {(["en", "es"] as const).map((lang) => (
                              <button
                                key={lang}
                                type="button"
                                onClick={() => setEditI18nLang((p) => p === lang ? null : lang)}
                                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${editI18nLang === lang ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400"}`}
                              >
                                {lang === "en" ? "🇬🇧 EN" : "🇪🇸 ES"}
                              </button>
                            ))}
                          </div>
                        </div>
                        {editI18nLang && (
                          <div className="grid gap-3">
                            <div>
                              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Başlık ({editI18nLang.toUpperCase()})</label>
                              <input
                                className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                                value={(editI18nLang === "en" ? (editingItem as any).title_en : (editingItem as any).title_es) ?? ""}
                                onChange={(e) => setEditingItem((p) => p && { ...p, [`title_${editI18nLang}`]: e.target.value })}
                                placeholder={editI18nLang === "en" ? "English title..." : "Título en español..."}
                              />
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Açıklama ({editI18nLang.toUpperCase()})</label>
                              <textarea
                                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                                rows={2}
                                value={(editI18nLang === "en" ? (editingItem as any).description_en : (editingItem as any).description_es) ?? ""}
                                onChange={(e) => setEditingItem((p) => p && { ...p, [`description_${editI18nLang}`]: e.target.value })}
                                placeholder={editI18nLang === "en" ? "English description..." : "Descripción en español..."}
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Her frekans süresi (saniye) *</label>
                        <input
                          type="number"
                          min="1"
                          max="3600"
                          className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                          value={editingItem.sure_saniye}
                          onChange={(e) => setEditingItem((p) => p && { ...p, sure_saniye: parseFloat(e.target.value) || 30 })}
                          required
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">XP Değeri</label>
                          <input
                            type="number"
                            min="0"
                            className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                            value={editingItem.xp ?? 0}
                            onChange={(e) => setEditingItem((p) => p && { ...p, xp: parseInt(e.target.value) || 0 })}
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Ürün Kademesi</label>
                          <select
                            className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                            value={editingItem.store_product_id ?? (editingItem.price ? "custom" : "free")}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === "free") {
                                setEditingItem((p) => p && { ...p, price: 0, store_product_id: null });
                              } else if (v === "custom") {
                                setEditingItem((p) => p && { ...p, store_product_id: null });
                              } else {
                                const tier = PRICE_TIERS.find((t) => t.storeProductId === v);
                                if (tier) setEditingItem((p) => p && { ...p, price: tier.price, store_product_id: tier.storeProductId });
                              }
                            }}
                          >
                            <option value="free">Ücretsiz</option>
                            {PRICE_TIERS.map((t) => (
                              <option key={t.storeProductId} value={t.storeProductId}>{t.label}</option>
                            ))}
                            <option value="custom">Serbest / Elle gir</option>
                          </select>
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Fiyat (₺) — 0 ise ücretsiz</label>
                          <input
                            type="number"
                            min="0"
                            disabled={Boolean(editingItem.store_product_id)}
                            className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/30 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                            value={editingItem.price ?? 0}
                            onChange={(e) => setEditingItem((p) => p && { ...p, price: parseInt(e.target.value) || 0 })}
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Program Günü (opsiyonel)</label>
                          <input
                            type="number"
                            min="0"
                            className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                            value={editingItem.program_gun ?? ""}
                            onChange={(e) => setEditingItem((p) => p && { ...p, program_gun: e.target.value !== "" ? parseInt(e.target.value) : undefined })}
                            placeholder="örn: 1"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Görsel (opsiyonel)</label>
                        <div className="flex items-center gap-3">
                          {editingItem.gorsel_url && (
                            <img src={editingItem.gorsel_url} alt="" className="h-14 w-14 flex-shrink-0 rounded-xl object-cover" />
                          )}
                          <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                            {editImgLoading ? "Yükleniyor…" : editingItem.gorsel_url ? "Değiştir" : "Görsel Seç"}
                            <input type="file" accept="image/*" className="hidden" onChange={handleEditImagePick} disabled={editImgLoading} />
                          </label>
                          {editingItem.gorsel_url && (
                            <button type="button" className="text-rose-400 hover:text-rose-600" onClick={() => setEditingItem((p) => p && { ...p, gorsel_url: "" })}>
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Etiketler</label>
                        <div className="flex flex-wrap gap-1.5 rounded-xl border border-slate-200 bg-slate-50 p-2 dark:border-slate-700 dark:bg-slate-800">
                          {(editingItem.tags ?? []).map((tag, i) => (
                            <span key={i} className="flex items-center gap-1 rounded-full bg-blue-600/10 px-2 py-0.5 text-xs font-semibold text-blue-600 dark:text-blue-300">
                              {tag}
                              <button type="button" onClick={() => setEditingItem((p) => p && { ...p, tags: (p.tags ?? []).filter((_, ti) => ti !== i) })} className="leading-none opacity-70 hover:opacity-100">×</button>
                            </span>
                          ))}
                          <input
                            className="min-w-[120px] flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400 dark:text-white"
                            placeholder="Etiket yaz, Enter'a bas..."
                            onKeyDown={(e) => {
                              const input = e.currentTarget;
                              if ((e.key === "Enter" || e.key === ",") && input.value.trim()) {
                                e.preventDefault();
                                const val = input.value.trim().replace(/,$/, "");
                                if (val && !(editingItem.tags ?? []).includes(val)) {
                                  setEditingItem((p) => p && { ...p, tags: [...(p.tags ?? []), val] });
                                }
                                input.value = "";
                              }
                            }}
                          />
                        </div>
                        <div className="mt-1 text-[11px] text-slate-400">Enter veya virgül ile ekle.</div>
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Kategori</label>
                        <select
                          key={frekansKategoriler.length}
                          className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                          value={editingItem.kategori_id ?? ''}
                          onChange={(e) => setEditingItem((p) => p && { ...p, kategori_id: e.target.value || undefined })}
                        >
                          <option value="">— Kategori seç —</option>
                          {frekansKategoriler.map((k: any) => (
                            <option key={k.id} value={k.id}>{k.isim}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Doğa Sesi</label>
                        <div className="flex items-center gap-2">
                          <select
                            className="h-10 flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                            value={natureUrlToFile(editingItem.nature_sound_url) ?? ""}
                            onChange={(e) => {
                              const file = e.target.value || null;
                              const label = NATURE_SOUNDS.find((s) => s.file === file)?.label ?? null;
                              setEditingItem((p) => p && { ...p, nature_sound_url: natureFileToUrl(file), nature_sound_label: label });
                            }}
                          >
                            {NATURE_SOUNDS.map((s) => (
                              <option key={s.file ?? "__none"} value={s.file ?? ""}>{s.label}</option>
                            ))}
                            {editingItem.nature_sound_url && !natureUrlToFile(editingItem.nature_sound_url) && (
                              <option value="__custom">Özel ses ({editingItem.nature_sound_label ?? editingItem.nature_sound_url.split("/").pop()})</option>
                            )}
                          </select>
                          <label className="flex cursor-pointer items-center gap-1.5 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 whitespace-nowrap">
                            {editSoundLoading ? "Yükleniyor…" : "Ses Yükle"}
                            <input type="file" accept="audio/*" className="hidden" onChange={handleEditSoundPick} disabled={editSoundLoading} />
                          </label>
                        </div>
                        {editingItem.nature_sound_url && (
                          <div className="mt-1 text-[11px] text-slate-400 break-all">{editingItem.nature_sound_url}</div>
                        )}
                      </div>

                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">Premium</div>
                          <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Sadece premium üyeler görebilir.</div>
                        </div>
                        <label className="relative inline-flex cursor-pointer items-center">
                          <input
                            type="checkbox"
                            className="peer sr-only"
                            checked={editingItem.is_premium ?? false}
                            onChange={(e) => setEditingItem((p) => p && { ...p, is_premium: e.target.checked })}
                          />
                          <div className="h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-amber-500 peer-checked:after:translate-x-full dark:bg-slate-800" />
                        </label>
                      </div>
                    </div>

                    {updateMutation.isError && (
                      <div className="mt-3 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                        {(updateMutation.error as Error).message}
                      </div>
                    )}
                    </div>

                    <div className="flex flex-shrink-0 gap-2 rounded-b-2xl border-t border-slate-200 bg-white p-6 pt-4 dark:border-slate-800 dark:bg-slate-900">
                      <Button type="submit" size="sm" className="rounded-full" disabled={updateMutation.isPending}>
                        {updateMutation.isPending ? "Kaydediliyor…" : "Kaydet"}
                      </Button>
                      <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => setEditingItem(null)}>
                        İptal
                      </Button>
                    </div>
                  </form>
                </div>
              )}

              {/* Liste */}
              {viewMode === "grid" ? (
                <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {visibleItems.map((item) => (
                    <div
                      key={item.id}
                      className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-900/40"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-3 min-w-0">
                          {item.gorsel_url && (
                            <img
                              src={item.gorsel_url}
                              alt=""
                              className="h-16 w-16 flex-shrink-0 rounded-xl object-cover"
                            />
                          )}
                          <div className="font-bold text-slate-900 dark:text-white">{item.baslik}</div>
                        </div>
                        <div className="flex flex-shrink-0 items-center gap-1">
                          <button
                            type="button"
                            className="rounded-xl p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                            onClick={() => setEditingItem(item)}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            className="rounded-xl p-1.5 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                            disabled={deleteMutation.isPending}
                            onClick={() => {
                              if (!confirm("Bu frekans setini silmek istiyor musunuz?")) return;
                              deleteMutation.mutate(item.id);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {item.aciklama && (
                        <div className="mt-1 line-clamp-2 text-sm text-slate-500 dark:text-slate-400">{item.aciklama}</div>
                      )}

                      <div className="mt-3 flex flex-wrap gap-1">
                        {(item.frekanslar ?? []).map((f, i) => {
                          const obj = typeof f === 'number' ? { base: f } : f as FrekansObj;
                          return (
                            <span
                              key={i}
                              className="rounded-full bg-blue-600/10 px-2 py-0.5 text-[11px] font-bold text-blue-600 dark:text-blue-300"
                            >
                              {obj.base} Hz{obj.beat ? ` + ${obj.beat} beat` : ""}
                            </span>
                          );
                        })}
                      </div>

                      {(item.tags ?? []).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {(item.tags ?? []).map((t, i) => (
                            <span key={i} className="rounded-full bg-violet-600/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-violet-600 dark:text-violet-300">
                              {t}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="mt-2 text-xs text-slate-400">
                        Her frekans: {item.sure_saniye}s • Toplam: {((item.frekanslar?.length ?? 0) * item.sure_saniye)}s
                      </div>

                      {item.ses_url && (
                        <button
                          type="button"
                          onClick={() => handlePlay(item.ses_url!)}
                          className="mt-3 flex items-center gap-2 rounded-xl bg-blue-600/10 px-3 py-1.5 text-sm font-semibold text-blue-600 hover:bg-blue-600/20 dark:text-blue-300"
                        >
                          <Play className="h-3.5 w-3.5" />
                          {playingUrl === item.ses_url ? "Durdur" : "Oynat"}
                        </button>
                      )}
                    </div>
                  ))}

                  {items.length === 0 && !showForm && (
                    <button
                      type="button"
                      onClick={() => setShowForm(true)}
                      className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 bg-white p-8 text-slate-500 hover:border-blue-400/50 hover:bg-blue-50/50 dark:border-slate-800 dark:bg-slate-950/40 dark:hover:bg-slate-900/30"
                    >
                      <Plus className="mb-2 h-8 w-8" />
                      <div className="font-bold">İlk frekans setini ekle</div>
                    </button>
                  )}
                </div>
              ) : (
                <div className="mt-6 flex flex-col gap-2">
                  {visibleItems.map((item) => (
                    <div
                      key={item.id}
                      className="group flex overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md dark:border-slate-800 dark:bg-slate-950/40"
                    >
                      <div
                        className="relative w-24 flex-none bg-gradient-to-br from-violet-600/25 via-blue-600/10 to-slate-950/20 sm:w-36"
                        style={item.gorsel_url ? { backgroundImage: `url(${item.gorsel_url})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
                      />

                      <div className="flex flex-1 items-center gap-3 px-4 py-3">
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-bold text-slate-900 dark:text-white">{item.baslik}</div>
                          {item.aciklama && (
                            <div className="mt-0.5 line-clamp-2 text-xs text-slate-500 dark:text-slate-400">{item.aciklama}</div>
                          )}
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {(item.frekanslar ?? []).slice(0, 3).map((f, i) => {
                              const obj = typeof f === 'number' ? { base: f } : f as FrekansObj;
                              return (
                                <span key={i} className="rounded-full bg-blue-600/10 px-2 py-0.5 text-[10px] font-bold text-blue-600 dark:text-blue-300">
                                  {obj.base} Hz{obj.beat ? ` +${obj.beat}` : ""}
                                </span>
                              );
                            })}
                            {(item.tags ?? []).slice(0, 2).map((t, i) => (
                              <span key={`tag-${i}`} className="rounded-full bg-violet-600/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-violet-600 dark:text-violet-300">
                                {t}
                              </span>
                            ))}
                          </div>
                        </div>

                        <div className="hidden flex-none sm:block">
                          <div className="rounded-xl bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                            {item.sure_saniye}s × {item.frekanslar?.length ?? 0}
                          </div>
                        </div>

                        <div className="flex flex-none items-center gap-1">
                          {item.ses_url && (
                            <button
                              type="button"
                              onClick={() => handlePlay(item.ses_url!)}
                              className="grid h-8 w-8 place-items-center rounded-xl bg-blue-600/10 text-blue-600 hover:bg-blue-600/20 dark:text-blue-300"
                            >
                              <Play className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <button
                            type="button"
                            className="grid h-8 w-8 place-items-center rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                            onClick={() => setEditingItem(item)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            className="grid h-8 w-8 place-items-center rounded-xl bg-slate-100 text-rose-600 hover:bg-rose-50 dark:bg-slate-800 dark:hover:bg-rose-900/20"
                            disabled={deleteMutation.isPending}
                            onClick={() => {
                              if (!confirm("Bu frekans setini silmek istiyor musunuz?")) return;
                              deleteMutation.mutate(item.id);
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {items.length === 0 && !showForm && (
                    <button
                      type="button"
                      onClick={() => setShowForm(true)}
                      className="flex items-center gap-4 rounded-2xl border-2 border-dashed border-slate-200 bg-white px-6 py-4 text-slate-600 transition-colors hover:border-blue-500/50 hover:bg-blue-50/50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300"
                    >
                      <div className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-50">
                        <span className="text-xl">+</span>
                      </div>
                      <div className="text-sm font-bold">İlk frekans setini ekle</div>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
